import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import axiosInstance from "../../API/axiosConfig";
import webserviceUrl from "../../API/webserviceUrl";
import { employee_id, school_code } from "../../utils/constant";
import { toast } from "react-toastify";
import Loading from "../loading/Loading";
import DeleteModal from "../../core/modals/deleteModal";
import { McitiImg } from "../../utils/iconLogo";

type RenderUploadedFilesProps = {
  files: any[];
  type: "local" | "server";
};

const TrainerCvSubmission = () => {
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [trainerDocFiles, setTrainerDocFiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "local" | "server";
    index?: number;
    form_upload_id?: string;
  } | null>(null);

  const formData = {
    school_code: school_code,
    employee_id: employee_id,
  };

  // ---------------- fetch existing Trainer CV files ----------------
  const getTrainerDocs = async () => {
    try {
      setIsLoading(true);
      const resp = await axiosInstance.post(
        webserviceUrl.get_trainer_submission,
        {
          school_code: school_code || "",
          employee_id: employee_id || "",
          document_name: "Trainer CV",
        },
      );
      const data = resp?.data?.data || [];
      setTrainerDocFiles(data);
    } catch (error) {
      console.error("Error fetching trainer CV:", error);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  useEffect(() => {
    getTrainerDocs();
  }, []);

  // ---------------- file select ----------------
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setUploadedFiles((prev) => [...prev, ...fileArray]);
    }
    event.target.value = ""; // allow re-selecting the same file
  };

  const handleFileDelete = (indexToDelete: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== indexToDelete));
  };

  // ---------------- submit ----------------
  const handleSubmit = async () => {
    if (uploadedFiles.length === 0) {
      toast.error("Please upload your CV file");
      return;
    }

    const submitData = new FormData();
    // submitData.append("school_code", formData.school_code || "");
    // submitData.append("employee_id", formData.employee_id || "");
    submitData.append("document_name", "Trainer CV");
    submitData.append("form_seq_id", "7");
    uploadedFiles.forEach((file) => {
      submitData.append("upload_file_path", file);
    });

    try {
      setIsSubmitting(true);
      const resp = await axiosInstance.post(
        webserviceUrl.upload_student_documents,
        submitData,
      );
      if (resp?.data?.success) {
        toast.success(
          resp?.data?.message || "Trainer CV uploaded successfully!",
        );
        setUploadedFiles([]);
        getTrainerDocs();
      } else {
        toast.error(resp?.data?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading trainer CV:", error);
      toast.error("Something went wrong while uploading");
    } finally {
      setTimeout(() => setIsSubmitting(false), 500);
    }
  };

  // ---------------- delete from server ----------------
  const handleDeleteServerFile = async (Id: string) => {
    try {
      const resp = await axiosInstance.post(
        webserviceUrl.delete_trainer_submission,
        {
          school_code: school_code || "",
          form_upload_id: Id || "",
        },
      );
      if (resp?.data?.success) {
        toast.success(resp?.data?.message || "File deleted successfully!");
        getTrainerDocs();
      }
    } catch (error) {
      console.error("Error deleting trainer CV:", error);
    }
  };

  const onConfirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === "local" && deleteTarget.index !== undefined) {
      handleFileDelete(deleteTarget.index);
    } else if (deleteTarget.type === "server" && deleteTarget.form_upload_id) {
      handleDeleteServerFile(deleteTarget.form_upload_id);
    }
    setDeleteTarget(null);
  };

  // ---------------- render table ----------------
  const RenderUploadedFiles: React.FC<RenderUploadedFilesProps> = ({
    files,
    type,
  }) => {
    if (!files || files.length === 0) return null;

    return (
      <div className="table-responsive mt-3">
        <table className="table table-bordered align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "5%" }}>SNo</th>
              <th>File Name</th>
              <th style={{ width: "10%" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file: any, index: number) => {
              const fileName =
                type === "local"
                  ? file?.name
                  : file?.upload_file_path?.split("/").pop() || "-";

              return (
                <tr key={type === "local" ? index : file?.form_upload_id}>
                  <td>{index + 1}</td>
                  <td>
                    <i className="ti ti-file-type-pdf text-danger me-2"></i>
                    {fileName}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary me-2"
                      title="View File"
                      onClick={() => {
                        if (type === "local") {
                          window.open(URL.createObjectURL(file), "_blank");
                        } else {
                          window.open(file?.upload_file_path, "_blank");
                        }
                      }}
                    >
                      <i className="ti ti-eye" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      data-bs-toggle="modal"
                      data-bs-target="#delete_modal"
                      disabled={isSubmitting}
                      title="Delete File"
                      onClick={() =>
                        setDeleteTarget({
                          type,
                          index,
                          form_upload_id: file?.form_upload_id,
                        })
                      }
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <DeleteModal handleDelete={onConfirmDelete} />

      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Trainer CV Submission</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/employee-dashboard">
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Teacher</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Trainer CV
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="card">
          <div className="card-body">
            <h4 className="mb-2">Trainer CV</h4>
            <p className="text-secondary mb-4">
              Upload the trainer's curriculum vitae (CV) in PDF format.
            </p>

            {/* Hidden File Input */}
            <input
              type="file"
              accept=".pdf"
              id="trainerCvUploadInput"
              multiple
              onChange={handleFileChange}
              className="d-none"
            />

            {/* Upload Button */}
            <button
              className="btn btn-outline-primary mb-3"
              onClick={() =>
                document.getElementById("trainerCvUploadInput")?.click()
              }
            >
              <i className="ti ti-plus me-1"></i> Upload CV
            </button>

            {/* Existing server files */}
            {isLoading ? (
              <Loading />
            ) : (
              trainerDocFiles?.length > 0 && (
                <RenderUploadedFiles files={trainerDocFiles} type="server" />
              )
            )}

            {/* Newly added local files */}
            {uploadedFiles?.length > 0 && (
              <>
                <h5 className="mt-4">Newly Added:</h5>
                <RenderUploadedFiles files={uploadedFiles} type="local" />
              </>
            )}

            {/* Submit */}
            <div className="mt-4" style={{ borderTop: "2px solid #ddd" }}>
              <div className="d-flex justify-content-end mt-3">
                <button
                  className="btn btn-primary border-0"
                  disabled={isSubmitting || isLoading}
                  onClick={handleSubmit}
                  style={{ backgroundColor: "green" }}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="footer d-smflex d-flex align-items-center justify-content-center border-top bg-white p-3 pb-0 pt-2">
        <p className="mb-0">
           Organized by IHFC, IIT 
          <img
            src={McitiImg}
            alt="Mciti Logo"
            className="ms-2"
            style={{ height: "40px", width: "40px", objectFit: "contain" }}
          />
        </p>
      </div>
    </div>
  );
};

export default TrainerCvSubmission;
